<template>
  <div class="lead-stats-page">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div v-if="admin_datos && admin_datos.user_type === 'lead_partner'" class="main-container">
        <div class="page-header">
          <div>
            <h1>Estadísticas del canal</h1>
            <p>Revisa evolución de solicitudes, ventas y conversión del lead partner.</p>
          </div>
          <img v-if="partnerLogo" :src="partnerLogo" alt="Logo partner" class="header-logo" />
        </div>

        <div class="filters-card">
          <label>
            <span>Fecha inicio</span>
            <input v-model="startDate" type="date" class="form-control" />
          </label>
          <label>
            <span>Fecha fin</span>
            <input v-model="endDate" type="date" class="form-control" />
          </label>
          <button class="btn btn-primary" :disabled="loading || !startDate || !endDate" @click="fetchStatistics">
            {{ loading ? 'Cargando...' : 'Actualizar' }}
          </button>
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <div class="summary-grid">
          <div class="summary-card">
            <span>Solicitudes</span>
            <strong>{{ summary.leads_count }}</strong>
          </div>
          <div class="summary-card">
            <span>Ventas</span>
            <strong>{{ summary.sales_count }}</strong>
          </div>
          <div class="summary-card">
            <span>Monto pagado</span>
            <strong>{{ formatCurrency(summary.sales_amount) }}</strong>
          </div>
          <div class="summary-card">
            <span>Conversión</span>
            <strong>{{ formatPercent(summary.conversion_rate) }}</strong>
          </div>
          <div class="summary-card">
            <span>Usos de cupones</span>
            <strong>{{ summary.coupon_usage_count }}</strong>
          </div>
          <div class="summary-card">
            <span>Ahorro clientes</span>
            <strong>{{ formatCurrency(summary.coupon_savings_amount) }}</strong>
          </div>
        </div>

        <div class="charts-grid">
          <section class="chart-card">
            <h2>Solicitudes por día</h2>
            <line-chart v-if="chartDataLeads.labels.length" :chart-data="chartDataLeads" :options="singleAxisOptions" />
            <p v-else class="empty-text">No hay solicitudes en el rango seleccionado.</p>
          </section>

          <section id="ventas-generadas" class="chart-card">
            <h2>Ventas y monto por día</h2>
            <line-chart v-if="chartDataSales.labels.length" :chart-data="chartDataSales" :options="dualAxisOptions" />
            <p v-else class="empty-text">No hay ventas en el rango seleccionado.</p>
          </section>
        </div>

        <section class="chart-card coupon-card">
          <h2>Cupones asociados</h2>
          <div v-if="couponUsage.length === 0" class="empty-text">No hay cupones asociados o usados en el rango seleccionado.</div>
          <div v-else class="table-wrap">
            <table class="tablex">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Regla descuento</th>
                  <th>Estado</th>
                  <th>Usos</th>
                  <th>Ahorro clientes</th>
                  <th>Último uso</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="coupon in couponUsage" :key="coupon.code">
                  <td><span class="coupon-code">{{ coupon.code }}</span></td>
                  <td>{{ coupon.discount_rule_name || 'Sin regla de descuento' }}</td>
                  <td>
                    <span class="status-pill" :class="{ inactive: coupon.discount_rule_active === false, missing: coupon.discount_rule_active == null }">
                      {{ coupon.discount_rule_active == null ? 'Sin regla' : (coupon.discount_rule_active === false ? 'Inactiva' : 'Activa') }}
                    </span>
                  </td>
                  <td>{{ coupon.usage_count }}</td>
                  <td>{{ formatCurrency(coupon.savings_amount) }}</td>
                  <td>{{ formatDate(coupon.last_used_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div v-else class="main-container">
        <p>No tienes permisos para acceder a esta sección.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'
import LineChart from '@/components/charts/LineChart.js'

function getISODateString (date) {
  const offset = date.getTimezoneOffset() * 60000
  const adjusted = new Date(date.getTime() - offset)
  return adjusted.toISOString().split('T')[0]
}

function generateDateRange (startDateStr, endDateStr) {
  const dates = []
  let currentDate = new Date(startDateStr + 'T00:00:00')
  const endDate = new Date(endDateStr + 'T00:00:00')
  if (isNaN(currentDate.getTime()) || isNaN(endDate.getTime()) || currentDate > endDate) {
    return []
  }
  while (currentDate <= endDate) {
    dates.push(getISODateString(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return dates
}

export default {
  name: 'LeadPartnerStatistics',
  components: {
    SideNav,
    TopNav,
    LineChart
  },
  data () {
    return {
      loading: false,
      error: '',
      startDate: '',
      endDate: '',
      summary: {
        leads_count: 0,
        sales_count: 0,
        sales_amount: 0,
        conversion_rate: 0,
        coupon_usage_count: 0,
        coupon_savings_amount: 0
      },
      couponUsage: [],
      chartDataLeads: { labels: [], datasets: [] },
      chartDataSales: { labels: [], datasets: [] },
      singleAxisOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 1
            }
          }]
        }
      },
      dualAxisOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true },
        scales: {
          yAxes: [
            {
              id: 'y-axis-amount',
              type: 'linear',
              position: 'left',
              ticks: {
                beginAtZero: true,
                callback: (value) => '$' + Number(value || 0).toLocaleString('es-CL')
              }
            },
            {
              id: 'y-axis-count',
              type: 'linear',
              position: 'right',
              ticks: {
                beginAtZero: true,
                stepSize: 1
              },
              gridLines: {
                drawOnChartArea: false
              }
            }
          ]
        }
      }
    }
  },
  computed: {
    ...mapGetters(['admin_datos']),
    partner () {
      return this.admin_datos && this.admin_datos.lead_partner ? this.admin_datos.lead_partner : null
    },
    partnerLogo () {
      const atributos = this.partner && this.partner.atributos ? this.partner.atributos : {}
      return atributos.logo_filename || atributos.logo_url || atributos.logo || ''
    }
  },
  created () {
    const today = new Date()
    const start = new Date(today)
    start.setDate(today.getDate() - 29)
    this.startDate = getISODateString(start)
    this.endDate = getISODateString(today)
    this.fetchStatistics()
  },
  methods: {
    async fetchStatistics () {
      if (!this.startDate || !this.endDate) {
        return
      }
      this.loading = true
      this.error = ''
      try {
        const response = await axios.get(window.hostname + 'api/lead_partner/statistics', {
          params: {
            start_date: this.startDate,
            end_date: this.endDate
          }
        })
        const data = response.data || {}
        this.summary = data.summary || this.summary
        this.couponUsage = data.coupon_usage || []
        this.prepareCharts(data)
      } catch (e) {
        this.error = e.response && e.response.data && (e.response.data.error || e.response.data.message)
          ? (e.response.data.error || e.response.data.message)
          : 'No se pudieron cargar las estadísticas.'
        this.chartDataLeads = { labels: [], datasets: [] }
        this.chartDataSales = { labels: [], datasets: [] }
        this.couponUsage = []
      } finally {
        this.loading = false
      }
    },
    prepareCharts (data) {
      const labels = generateDateRange(this.startDate, this.endDate)
      const leadsMap = {}
      const salesMap = {}

      ;(data.leads_time_series || []).forEach((row) => {
        leadsMap[row.date] = Number(row.count || 0)
      })
      ;(data.sales_time_series || []).forEach((row) => {
        salesMap[row.date] = {
          sales_count: Number(row.sales_count || 0),
          sales_amount: Number(row.sales_amount || 0)
        }
      })

      this.chartDataLeads = {
        labels,
        datasets: [
          {
            label: 'Solicitudes',
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.18)',
            fill: true,
            data: labels.map(label => leadsMap[label] || 0)
          }
        ]
      }

      this.chartDataSales = {
        labels,
        datasets: [
          {
            label: 'Monto pagado',
            borderColor: '#059669',
            backgroundColor: 'rgba(5, 150, 105, 0.14)',
            fill: true,
            yAxisID: 'y-axis-amount',
            data: labels.map(label => (salesMap[label] ? salesMap[label].sales_amount : 0))
          },
          {
            label: 'Ventas',
            borderColor: '#f97316',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
            fill: false,
            yAxisID: 'y-axis-count',
            data: labels.map(label => (salesMap[label] ? salesMap[label].sales_count : 0))
          }
        ]
      }
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(Number(value || 0))
    },
    formatPercent (value) {
      return `${Number(value || 0).toFixed(1)}%`
    },
    formatDate (value) {
      if (!value) return 'Sin uso'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.lead-stats-page {
  text-align: left;
}
.column-right {
  margin-left: 240px;
  padding: 88px 28px 40px;
}
.main-container {
  width: 100%;
  max-width: none;
}
.page-header,
.filters-card,
.summary-card,
.chart-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
}
.page-header {
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}
.page-header h1 {
  margin: 0;
}
.page-header p {
  margin: 8px 0 0;
  color: #64748b;
}
.header-logo {
  max-height: 64px;
  max-width: 180px;
  object-fit: contain;
}
.filters-card {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
}
.filters-card label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #475569;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.summary-card {
  padding: 20px;
}
.summary-card span {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 8px;
}
.summary-card strong {
  font-size: 28px;
  color: #0f172a;
}
.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}
.chart-card {
  padding: 20px;
  min-height: 360px;
}
.chart-card h2 {
  margin: 0 0 16px;
  font-size: 22px;
}
.coupon-card {
  min-height: auto;
  margin-top: 20px;
}
.table-wrap {
  overflow-x: auto;
}
.tablex {
  width: 100%;
  border-collapse: collapse;
}
.tablex th,
.tablex td {
  text-align: left;
  padding: 12px 10px;
  border-bottom: 1px solid #edf2f7;
}
.tablex th {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}
.coupon-code {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: 0;
  padding: 4px 8px;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
}
.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}
.status-pill.missing {
  background: #f1f5f9;
  color: #475569;
}
.empty-text {
  color: #64748b;
}
@media (max-width: 1400px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 1100px) {
  .charts-grid,
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 800px) {
  .column-right {
    margin-left: 0;
    padding: 88px 16px 32px;
  }
  .page-header,
  .filters-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
